/**
 * Menú de usuario de la barra superior
 * -----------------------------------------------------------------------------
 * Sub-componente reutilizable: el theme aporta la estructura y el estilo, y cada
 * aplicación pone sus datos y sus opciones. Aquí NO hay ítems de negocio —ni
 * «Cerrar sesión», ni «Mi perfil»—: los define quien lo use.
 *
 * Dos formas de usarlo, según cómo esté construida la app.
 *
 * 1. Markup, si la vista la pinta el servidor (Twig, PHP…):
 *
 *    <li class="nav-item dropdown owari-user-menu">
 *      <a class="nav-link dropdown-toggle hidden-arrow" href="#" data-mdb-dropdown-init>
 *        <span class="owari-user-avatar"><img src="…" alt=""></span>
 *        <span class="owari-user-text">
 *          <span class="owari-user-name">Miguel</span>
 *          <span class="owari-user-meta">Finca La Esperanza</span>
 *        </span>
 *        <i class="fas fa-chevron-down owari-user-caret"></i>
 *      </a>
 *      <ul class="dropdown-menu dropdown-menu-end">…</ul>
 *    </li>
 *
 * 2. Desde JS, si la app monta la barra dinámicamente:
 *
 *    CompanyTheme.mountUserMenu('#navbar-actions', {
 *      name: 'Miguel',
 *      meta: 'Finca La Esperanza',
 *      avatar: '/img/miguel.jpg',
 *      items: [
 *        { label: 'Mi perfil', href: '/perfil', icon: 'fa-user' },
 *        { divider: true },
 *        { label: 'Cerrar sesión', href: '/logout', icon: 'fa-right-from-bracket' },
 *      ],
 *    });
 */

let uid = 0;

/**
 * Iniciales para el avatar cuando no hay imagen. Toma la primera letra de las
 * dos primeras palabras: «Miguel Arcane» -> «MA».
 */
function initialsOf(name) {
  return String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
}

function escape(text) {
  const node = document.createElement('span');
  node.textContent = text == null ? '' : String(text);
  return node.innerHTML;
}

function renderItem(item) {
  if (item.divider) return '<li><hr class="dropdown-divider"></li>';

  const icon = item.icon ? `<i class="fas ${escape(item.icon)} fa-fw me-2"></i>` : '';
  const tag = item.href ? 'a' : 'button';
  const attrs = item.href
    ? `href="${escape(item.href)}"`
    : 'type="button"';

  return `<li><${tag} class="dropdown-item" ${attrs}>${icon}${escape(item.label)}</${tag}></li>`;
}

/**
 * Construye el menú y lo devuelve sin insertarlo.
 *
 * @param {Object} options
 * @param {string} options.name    Nombre visible.
 * @param {string} [options.meta]  Segunda línea: finca, rol, empresa…
 * @param {string} [options.avatar] URL de la imagen; si falta se usan iniciales.
 * @param {Array}  [options.items] Opciones del desplegable.
 * @returns {HTMLElement} Un `<li class="nav-item dropdown">` listo para insertar.
 */
export function createUserMenu({ name, meta, avatar, items = [] } = {}) {
  uid += 1;
  const id = `owari-user-menu-${uid}`;

  const element = document.createElement('li');
  element.className = 'nav-item dropdown owari-user-menu';

  const picture = avatar
    ? `<img src="${escape(avatar)}" alt="">`
    : `<span class="owari-user-initials">${escape(initialsOf(name))}</span>`;

  // La segunda línea y el desplegable son opcionales: hay apps sin más contexto
  // que el nombre, y otras sin acciones.
  const text = `
    <span class="owari-user-text">
      <span class="owari-user-name">${escape(name)}</span>
      ${meta ? `<span class="owari-user-meta">${escape(meta)}</span>` : ''}
    </span>`;

  element.innerHTML = `
    <a class="nav-link dropdown-toggle hidden-arrow" href="#" id="${id}" role="button"
       data-mdb-dropdown-init aria-expanded="false">
      <span class="owari-user-avatar">${picture}</span>
      ${text}
      ${items.length ? '<i class="fas fa-chevron-down owari-user-caret"></i>' : ''}
    </a>
    ${
      items.length
        ? `<ul class="dropdown-menu dropdown-menu-end" aria-labelledby="${id}">
            ${items.map(renderItem).join('')}
          </ul>`
        : ''
    }
  `;

  return element;
}

/**
 * Construye el menú y lo inserta en `container`.
 *
 * @param {string|HTMLElement} container Selector o elemento (normalmente el `<ul class="navbar-nav">`).
 * @param {Object} options Igual que `createUserMenu`.
 * @returns {HTMLElement|null} El menú insertado.
 */
export function mountUserMenu(container, options) {
  const target = typeof container === 'string' ? document.querySelector(container) : container;
  if (!target) return null;

  const menu = createUserMenu(options);
  target.appendChild(menu);

  return menu;
}

export default mountUserMenu;
